(() => {
  const euro = (value) => value.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' €';

  const parisDay = () => {
    try {
      return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });
    } catch (error) {
      return new Date().toISOString().slice(0, 10);
    }
  };

  const hash = (value) => {
    let result = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      result ^= value.charCodeAt(index);
      result = Math.imul(result, 16777619);
    }
    return result >>> 0;
  };

  const availableVariant = (product) => {
    const variants = (product.variants || []).filter((variant) => variant.available);
    variants.sort((first, second) => Number(first.price) - Number(second.price));
    return variants[0];
  };

  const productImage = (product, variant) => {
    const source = variant?.featured_image?.src || product.images?.[0]?.src || '';
    if (!source) return '';
    return source + (source.includes('?') ? '&width=600' : '?width=600');
  };

  const shortName = (title) => {
    const parts = String(title || '').split(' – ');
    return parts.length > 1 ? parts[1] : parts[0];
  };

  const selectDailyPack = (products, minimumTotal) => {
    const pool = products
      .map((product) => ({ product, variant: availableVariant(product) }))
      .filter((entry) => entry.variant
        && !/\b(?:500\s*g|1\s*kg|1000\s*g)\b/i.test(entry.product.title || '')
        && !/d[eé]caf/i.test(entry.product.title || ''))
      .sort((first, second) => Number(first.product.id) - Number(second.product.id));

    if (pool.length < 4) return [];

    const start = hash(`discovery-pack|${parisDay()}`) % pool.length;
    const rotated = pool.slice(start).concat(pool.slice(0, start));
    const chosen = [];
    const vendors = new Set();
    rotated.forEach((entry) => {
      const vendor = String(entry.product.vendor || '').toLowerCase();
      if (chosen.length < 4 && vendor && !vendors.has(vendor)) {
        chosen.push(entry);
        vendors.add(vendor);
      }
    });
    rotated.forEach((entry) => {
      if (chosen.length < 4 && !chosen.includes(entry)) chosen.push(entry);
    });
    const reserve = rotated.filter((entry) => !chosen.includes(entry));
    const total = () => chosen.reduce((sum, entry) => sum + Number(entry.variant.price), 0);

    while (total() < minimumTotal && reserve.length) {
      let cheapestIndex = 0;
      chosen.forEach((entry, index) => {
        if (Number(entry.variant.price) < Number(chosen[cheapestIndex].variant.price)) cheapestIndex = index;
      });
      const otherVendors = new Set(chosen
        .filter((entry, index) => index !== cheapestIndex)
        .map((entry) => String(entry.product.vendor || '').toLowerCase()));
      const eligible = reserve
        .map((entry, index) => ({ entry, index }))
        .filter((candidate) => !otherVendors.has(String(candidate.entry.product.vendor || '').toLowerCase())
          && Number(candidate.entry.variant.price) > Number(chosen[cheapestIndex].variant.price))
        .sort((first, second) => Number(first.entry.variant.price) - Number(second.entry.variant.price));
      if (!eligible.length) break;
      const requiredPrice = minimumTotal - (total() - Number(chosen[cheapestIndex].variant.price));
      const candidate = eligible.find((option) => Number(option.entry.variant.price) >= requiredPrice)
        || eligible[eligible.length - 1];
      const replacementIndex = candidate.index;
      const replacement = reserve.splice(replacementIndex, 1)[0];
      chosen[cheapestIndex] = replacement;
    }

    return chosen;
  };

  const updateProductCard = (card, entry) => {
    const { product, variant } = entry;
    const url = `/products/${product.handle}`;
    const image = card.querySelector('.kl-discovery-pack__photo');
    card.href = url;
    if (image) {
      image.removeAttribute('srcset');
      image.src = productImage(product, variant);
      image.alt = product.title || '';
    }
    const vendor = card.querySelector('[data-kl-pack-vendor]');
    const name = card.querySelector('[data-kl-pack-name]');
    const price = card.querySelector('[data-kl-pack-price]');
    if (vendor) vendor.textContent = product.vendor || '';
    if (name) name.textContent = product.title || '';
    if (price) price.textContent = euro(Number(variant.price));
  };

  const updateShelfCard = (card, entry) => {
    const { product, variant } = entry;
    const image = card.querySelector('[data-kl-shelf-image]');
    card.href = `/products/${product.handle}`;
    if (image) {
      image.removeAttribute('srcset');
      image.src = productImage(product, variant);
      image.alt = product.title || '';
    }
    const name = card.querySelector('[data-kl-shelf-name]');
    const price = card.querySelector('[data-kl-shelf-price]');
    if (name) name.textContent = shortName(product.title);
    if (price) price.textContent = euro(Number(variant.price));
  };

  const rotatePack = async () => {
    const section = document.querySelector('[data-kl-discovery-pack]');
    if (!section) return;

    try {
      const collection = section.dataset.collection;
      const response = await fetch(`/collections/${collection}/products.json?limit=250`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const data = await response.json();
      const minimumTotal = Number(section.dataset.minimumTotal || 66.67);
      const selection = selectDailyPack(data.products || [], minimumTotal);
      if (selection.length !== 4) return;

      const packCards = [...section.querySelectorAll('[data-kl-pack-product]')];
      const shelfCards = [...document.querySelectorAll('[data-kl-shelf-card]')];
      packCards.slice(0, 4).forEach((card, index) => updateProductCard(card, selection[index]));
      shelfCards.slice(0, 4).forEach((card, index) => updateShelfCard(card, selection[index]));

      const itemsNode = section.querySelector('[data-kl-discovery-items]');
      if (itemsNode) {
        itemsNode.textContent = JSON.stringify(selection.map((entry) => ({
          id: entry.variant.id,
          quantity: 1,
        })));
      }

      const total = selection.reduce((sum, entry) => sum + Number(entry.variant.price), 0);
      const formattedTotal = euro(total);
      const button = section.querySelector('[data-kl-discovery-add]');
      if (button) {
        const nextLabel = button.dataset.labelTemplate.replace('__PRICE__', formattedTotal);
        button.dataset.labelDefault = nextLabel;
        const label = button.querySelector('[data-kl-discovery-button-label]');
        if (label) label.textContent = nextLabel;
      }
      const heroOffer = document.querySelector('[data-kl-pack-hero-offer]');
      if (heroOffer) heroOffer.textContent = heroOffer.dataset.offerTemplate.replace('__PRICE__', formattedTotal);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'kl_discovery_pack_viewed',
        item_ids: selection.map((entry) => String(entry.product.id)),
        value: total,
        source: 'homepage_daily_pack',
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handlePackAdd = async (button) => {
    const section = button.closest('[data-kl-discovery-pack]');
    const itemsNode = section?.querySelector('[data-kl-discovery-items]');
    const label = button.querySelector('[data-kl-discovery-button-label]');

    if (!section || !itemsNode || !label || button.disabled) return;

    let items;
    try {
      items = JSON.parse(itemsNode.textContent);
    } catch (error) {
      console.error(error);
      return;
    }

    const setLabel = (state) => {
      label.textContent = button.dataset[`label${state}`] || button.dataset.labelDefault;
    };

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    setLabel('Loading');

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const sections = cartDrawer
        ? cartDrawer.getSectionsToRender().map((entry) => entry.id)
        : ['cart-drawer', 'cart-icon-bubble'];
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const cartState = await response.json();

      if (!response.ok || cartState.status) {
        throw new Error(cartState.description || cartState.message || 'Cart error');
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'kl_discovery_pack_added',
        item_count: items.length,
        source: 'homepage_discovery_pack',
      });
      document.dispatchEvent(new CustomEvent('koffyland:discovery-pack-added', { detail: cartState }));

      setLabel('Success');
      if (cartDrawer && cartState.sections) {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.setActiveElement(button);
        cartDrawer.renderContents(cartState);
      } else {
        window.location.assign(window.routes.cart_url);
      }
    } catch (error) {
      console.error(error);
      setLabel('Error');
      window.setTimeout(() => setLabel('Default'), 2200);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-kl-discovery-add]');
    if (button) handlePackAdd(button);
  });

  rotatePack();
})();
